import { Badge } from '@/components/ui/badge';
import { trackEvent } from '@/utils/analytics';
import { getAllBlogPosts } from '@/utils/blog/queries';
import { BlogPost } from '@/utils/blog/types';
import { calculateReadTime } from '@/utils/blogUtils';
import { Calendar, Clock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * Componente Blog - Página de listagem de artigos do blog
 */
const Blog = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Blog | Perícia Técnica";
    trackEvent('PageView', 'Blog', 'Blog Page View');
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const posts = await getAllBlogPosts();
      // Sort by publish date (newest first)
      const sortedPosts = posts.sort((a, b) => 
        new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime()
      );
      setBlogPosts(sortedPosts);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostClick = (postId: string, postTitle: string) => {
    trackEvent('BlogInteraction', 'PostClick', `Post: ${postTitle} (${postId})`);
  };

  const handleTagClick = (tag: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent('BlogInteraction', 'TagFilter', `Tag: ${tag}`);
    setActiveTag(activeTag === tag ? null : tag);
  };

  const filteredPosts = activeTag 
    ? blogPosts.filter(post => post.tags.includes(activeTag))
    : blogPosts;

  return (
    <>
      {/* Optimized SEO metadata for blog listing page */}
      <Helmet>
        <title>Blog de Perícia Judicial e Engenharia | Técnicas e Análises</title>
        <meta 
          name="description" 
          content="Artigos especializados sobre perícia judicial, engenharia civil, assistência técnica, laudos e vistorias técnicas. Aprenda com especialistas sobre perícias e avaliações imobiliárias."
        />
        <meta name="keywords" content="blog perícia judicial, artigos engenharia civil, laudos técnicos, vistorias, avaliações imobiliárias, assistência técnica judicial" />
        
        {/* Tags Open Graph for social sharing */}
        <meta property="og:title" content="Blog de Perícia Judicial e Engenharia | Técnicas e Análises" />
        <meta property="og:description" content="Artigos especializados sobre perícia judicial, engenharia civil, assistência técnica, laudos e vistorias técnicas." />
        <meta property="og:url" content="https://www.periciatecnica.eng.br/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.periciatecnica.eng.br/assets/images/blog/og-image-blog.jpg" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog de Perícia Judicial e Engenharia | Técnicas e Análises" />
        <meta name="twitter:description" content="Artigos especializados sobre perícia judicial, engenharia civil, assistência técnica, laudos e vistorias técnicas." />
        <meta name="twitter:image" content="https://www.periciatecnica.eng.br/assets/images/blog/og-image-blog.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.periciatecnica.eng.br/blog" />
        
        {/* Schema.org markup for blog */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Blog",
              "name": "Blog de Perícia Judicial e Engenharia",
              "description": "Artigos especializados sobre perícia judicial, engenharia civil, assistência técnica, laudos e vistorias técnicas.",
              "url": "https://www.periciatecnica.eng.br/blog",
              "publisher": {
                "@type": "Organization",
                "name": "Moacyr Moura Ferreira - Perícia Técnica",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://www.periciatecnica.eng.br/assets/images/logo.png"
                }
              },
              "blogPost": [
              ${blogPosts.map(post => `
                {
                  "@type": "BlogPosting",
                  "headline": "${post.title}",
                  "description": "${post.excerpt}",
                  "url": "https://www.periciatecnica.eng.br/blog/${post.slug}",
                  "datePublished": "${new Date(post.publish_date).toISOString()}",
                  "author": {
                    "@type": "Person",
                    "name": "${post.author}"
                  },
                  "image": "${post.image}",
                  "keywords": "${post.tags.join(', ')}"
                }
              `).join(',')}
              ]
            }
          `}
        </script>
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.periciatecnica.eng.br/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://www.periciatecnica.eng.br/blog"
                }
              ]
            }
          `}
        </script>
        
        {/* CollectionPage Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "headline": "Blog de Perícia Judicial e Engenharia | Técnicas e Análises",
              "description": "Artigos especializados sobre perícia judicial, engenharia civil, assistência técnica, laudos e vistorias técnicas.",
              "url": "https://www.periciatecnica.eng.br/blog",
              "mainEntity": {
                "@type": "ItemList",
                "itemListElement": [
                  ${blogPosts.map((post, index) => `
                    {
                      "@type": "ListItem",
                      "position": ${index + 1},
                      "url": "https://www.periciatecnica.eng.br/blog/${post.slug}"
                    }
                  `).join(',')}
                ]
              }
            }
          `}
        </script>
      </Helmet>
      
      {/* Main blog section */}
      <div className="pt-24 pb-16 bg-gray-50 dark:bg-gradient-to-b dark:from-gray-950 dark:to-black">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Page header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Blog de Perícia Judicial e Engenharia</h1>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Artigos especializados sobre perícias judiciais, assistência técnica e outros temas relacionados à engenharia civil.
            </p>
          </div>
          
          {/* Blog post grid - responsive */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-300 dark:bg-gray-700 aspect-video rounded-t-xl"></div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-b-xl">
                    <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-full mb-3"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-4/5"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group rounded-xl overflow-hidden hover:transform hover:-translate-y-1 transition-all duration-300"
                  onClick={() => handlePostClick(post.id, post.title)}
                >
                  <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800/50 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                    {/* Image container with fixed aspect ratio */}
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
                        width="600"
                        height="338"
                        loading="lazy"
                      />
                      {/* Image overlay gradient on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    {/* Post card content */}
                    <div className="p-6">
                      {/* Tags - limited to 2 for clean layout */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 2).map((tag) => (
                          <Badge 
                            key={tag} 
                            variant="secondary"
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800/40 px-3 py-1 text-sm font-medium shadow-sm"
                            onClick={(e) => handleTagClick(tag, e)}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Post title with hover effect */}
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {post.title}
                      </h2>
                      
                      {/* Post excerpt limited to 2 lines */}
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      
                      {/* Post metadata (date and read time) */}
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-500 pt-4 border-t border-gray-200 dark:border-gray-800/50">
                        <div className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {new Date(post.publish_date).toLocaleDateString('pt-BR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center">
                          <Clock size={14} className="mr-1" />
                          {post.readTime || calculateReadTime(post.content)} min
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Blog;
